import type { TTSLanguage, TTSVoice } from '~/models/Brikke/TextToSpeech'

export function useTextToSpeech() {
  const ignoredClasses = [
    // uu
    'sr-only',
    // pendo
    '_pendo-badge',
    // flyt
    'p-tag',
    '--text'
  ]

  const ignoredElements = [
    'video',
    'audio',
    'img',
    'svg',
    'iframe',
    'object',
    'input',
    'dialog',
    'pre',
    'code',
    'media-player',
    '[data-ignore-tts]',
  ]

  const punctuatedElements = [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'li',
    'div',
    'blockquote',
    'label',
    'button',
    'small',
    'span',
    'strong',
    'th',
    'td',
  ]

  const findText = (selector: string) => {
    const content = document.querySelector(selector)
    if (!content)
      return ''

    const clonedContent = content.cloneNode(true) as HTMLElement
    const nodesToBeIgnored = clonedContent.querySelectorAll(ignoredElements.join(', '))
    nodesToBeIgnored.forEach(node => node.remove())
    return replaceSpecialSigns(parseHtml(clonedContent).join(' ').trim())
  }

  const getCacheKeys = (selector: string) => {
    const root = document.querySelector(selector)
    if (!root)
      return ''

    const elements = root.querySelectorAll('[data-key-tts]')

    return Array.from(elements)
      .filter((el) => {
        const ignored = el.closest('[data-ignore-tts]')
        return !ignored || !root.contains(ignored)
      })
      .map(el => el.getAttribute('data-key-tts'))
      .join('')
  }

  const hasParentClass = (node: ChildNode, matchClasses: string[]) => {
    let current = node.parentElement
    while (current) {
      if (current.classList && Array.from(current.classList)
        .some(className => matchClasses.includes(className))) {
        return true
      }
      current = current.parentElement
    }
    return false
  }

  const needsPunctuation = (nodeName: string, text: string) => !',.!?:;/\\-*´\'\"'.includes(text.slice(-1))
    && punctuatedElements.includes(nodeName.toLowerCase())

  const parseHtml = (content: HTMLElement): string[] => {
    const textNodes: string[] = []

    // Only process text nodes
    const check = (child: ChildNode) => {
      if (
        child?.parentElement
        && child.nodeType === Node.TEXT_NODE
        && !hasParentClass(child, ignoredClasses)
      ) {
        const text = child.nodeValue?.trim()
        if (text && child.parentElement) {
          const punctuated = needsPunctuation(child.parentElement.nodeName, text) ? `${text.trim()}.` : text.trim()
          textNodes.push(punctuated)
        }
      } else {
        if (punctuatedElements.includes(child.nodeName.toLowerCase())) {
          textNodes.push('\n')
        }
        // Continue processing children
        child.childNodes.forEach(check)
      }
    }

    content.childNodes.forEach(check)
    return textNodes.filter(Boolean)
  }

  const replaceSpecialSigns = (text: string) => {
    return text
      .replaceAll(String.fromCharCode(160), ' ') // Replace non-breaking space
      .replaceAll(String.fromCharCode(97, 778), 'å') // Replace fake 'å' that really is 'a' + 'ring above'
  }

  const mapLanguageToVoice = (language: TTSLanguage): TTSVoice => {
    const map: Record<TTSLanguage, TTSVoice> = {
      nn_NO: 7389, // Daniel
      nb_NO: 7387, // Cathrin
      en_GB: 3555, // Ad
      fr_FR: 2524, // Remy
      de_DE: 2519, // Emma
      es_ES: 3562, // Isabella
    }
    return map[language]
  }

  return { findText, mapLanguageToVoice, getCacheKeys }
}
